import axios from 'axios';
import { API } from '../../../config/api';

const http = axios.create({
  baseURL: API.BACKEND,
  timeout: 15000
});

// `axios.create()` snapshots the global defaults, so a token written later by
// `setAuthToken` never reaches this instance. See features/jobs/api/http.js.
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

// Median cost per machine / reason / operator — what a forecast multiplies.
// Product and operator travel too: the Downtime tab shows this alongside the
// downtime matrix, which DOES narrow by both — two panels on one screen filtered
// differently, with nothing on screen saying so.
export async function fetchIntervalStats({
  machineId, productLabel, operator, operators, dateFrom, dateTo
} = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (productLabel) params.productLabel = productLabel;
  if (Array.isArray(operators) && operators.length) params.operators = operators.join(',');
  else if (operator) params.operator = operator;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/machine-intervals/stats', { params });
  return data;
}

// Raw intervals, newest first.
export async function fetchIntervals({ machineId, reason, operator, measured, dateFrom, dateTo, page, limit } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (reason) params.reason = reason;
  if (operator) params.operator = operator;
  if (measured !== undefined) params.measured = measured;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (page) params.page = page;
  if (limit) params.limit = limit;

  const { data } = await http.get('/api/v1/machine-intervals', { params });
  return data;
}

// Σ(metres/speed) + Σ(transition × its measured cost) for a planned run.
export async function postForecast({ machineId, operator, tokens, previousToken }) {
  const { data } = await http.post('/api/v1/machine-intervals/forecast', {
    machineId,
    operator: operator || null,
    tokens: tokens || [],
    previousToken: previousToken || null
  });
  return data;
}

// Per-drum estimate across the phases of a job. Each token carries its own
// machine, so every drum is costed on the line that will actually run it.
export async function postEstimateTokens({ tokens, jobIds, operator }) {
  const { data } = await http.post('/api/v1/machine-intervals/estimate-tokens', {
    tokens: tokens || [],
    jobIds: jobIds || [],
    operator: operator || null
  });
  return data;
}

/**
 * Map ProjectDetails execution rows into estimate input.
 * One row is already one drum on one phase, which is exactly the granularity
 * asked for — only finished drums are dropped, since they cost nothing more.
 */
export function projectRowsToEstimateTokens(rows = []) {
  return rows
    .filter(r => r && !r.isPhantomSource)
    .map(r => {
      const planned = Number(r.plannedLength) || 0;
      const produced = Number(r.executedLength) || 0;
      return {
        tokenId: r.outputs?.[0] || r.taskId || null,
        rowKey: r.rowKey,
        machineId: r.machineId || null,
        phase: r.phase || null,
        productLabel: r.productLabel || null,
        color: r.color || null,
        wireType: r.wireType || null,
        wireDiameter: r.wireDiameter ?? null,
        section: r.section ?? null,
        plannedM: planned,
        producedM: produced,
        started: produced > 0 || String(r.status || '').toUpperCase() === 'IN_PROGRESS'
      };
    })
    .filter(t => t.plannedM > t.producedM);
}

/**
 * Map machine-queue rows ({ group, task }) into the shape the forecast expects.
 *
 * A task can carry several drums; each one is its own transition, so they are
 * emitted separately — that is the whole point of costing colour changes between
 * drums of the same product.
 */
export function queueRowsToForecastTokens(rows = []) {
  const tokens = [];

  for (const row of rows) {
    const task = row?.task || row;
    if (!task) continue;

    const base = {
      productLabel: row?.group?.productLabel || task.productLabel || null,
      phase: task.phase || null,
      wireType: row?.group?.wireType || task.wireType || null,
      wireDiameter: Number(row?.group?.wireDiameter) || null,
      section: Number(row?.group?.section) || null
    };

    const drums = Array.isArray(task.tokens) && task.tokens.length ? task.tokens : null;

    if (drums) {
      for (const d of drums) {
        tokens.push({
          ...base,
          tokenId: d.tokenId || null,
          color: d.color || task.color || null,
          lengthM: Number(d.plannedLength ?? task.plannedLength) || 0
        });
      }
    } else {
      tokens.push({
        ...base,
        tokenId: task.tokenId || task.taskId || null,
        color: task.color || null,
        lengthM: Number(task.plannedLength) || 0
      });
    }
  }

  return tokens;
}

// Downtime × operator × product × phase × reason, with the same peer comparison.
export async function fetchDowntimeMatrix({ machineId, reason, productLabel, operator, operators, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (reason) params.reason = reason;
  if (productLabel) params.productLabel = productLabel;
  if (Array.isArray(operators) && operators.length) params.operators = operators.join(',');
  else if (operator) params.operator = operator;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/machine-intervals/downtime-matrix', { params });
  return data;
}
